import { useState, useEffect, useRef } from "react";

export const useGetScaleAfterRotation = (
  rotationAngleDeg,
  svgHeight,
  svgWidth
) => {
  const [scale, setScale] = useState(1);
  const [containerSize, setContainerSize] = useState(0);
  const svgFrameRef = useRef(null);

  const rotationAngleRad = rotationAngleDeg
    ? rotationAngleDeg * (Math.PI / 180)
    : 0;
  const ratio = svgHeight / svgWidth;

  const getScaledDimensions = (ratio, parentSize) => {
    const roundedRatio = Math.floor(ratio);
    return !roundedRatio
      ? [parentSize, parentSize * ratio]
      : roundedRatio === 1
      ? [parentSize, parentSize]
      : [parentSize / ratio, parentSize];
  };

  const rotateCoordinates = (x, y, angleRad) => {
    const newX = x * Math.cos(angleRad) - y * Math.sin(angleRad);
    const newY = x * Math.sin(angleRad) + y * Math.cos(angleRad);

    return [newX, newY];
  };

  useEffect(() => {
    const handleResize = () => {
      if (svgFrameRef.current) {
        const size = svgFrameRef.current.offsetWidth - 2 * 16; // Container size minus padding TODO: maybe use some global variable for padding?
        setContainerSize(size);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (containerSize > 0) {
      const svgRenderedDimensions = getScaledDimensions(ratio, containerSize);

      const svgCoordinates = [
        [
          -svgRenderedDimensions[0] / 2,
          Math.floor(svgRenderedDimensions[1]) / 2,
        ],
        [
          svgRenderedDimensions[0] / 2,
          Math.floor(svgRenderedDimensions[1]) / 2,
        ],
        [
          svgRenderedDimensions[0] / 2,
          -Math.floor(svgRenderedDimensions[1]) / 2,
        ],
        [
          -svgRenderedDimensions[0] / 2,
          -Math.floor(svgRenderedDimensions[1]) / 2,
        ],
      ];

      const rotatedCoordinates = svgCoordinates.map((point) =>
        rotateCoordinates(point[0], point[1], rotationAngleRad)
      );

      const farthestPointOnX = Math.max(
        ...rotatedCoordinates.map((point) => Math.abs(point[0]))
      );
      const farthestPointOnY = Math.max(
        ...rotatedCoordinates.map((point) => Math.abs(point[1]))
      );
      const rotatedWidth = 2 * farthestPointOnX;
      const rotatedHeight = 2 * farthestPointOnY;
      const newScale = Math.min(
        containerSize / rotatedWidth,
        containerSize / rotatedHeight
      );

      setScale(newScale);
    }
  }, [containerSize, svgHeight, svgWidth]);

  return { scale, svgFrameRef };
};
